// ============================================
// TABELAS PROGRESSIVAS - IMPOSTO DE RENDA
// ============================================

// Tabela IRRF 2025 - VIGENTE (Lei nº 14.848/2024)
export const TABELA_IRRF_2025_VIGENTE = [
  { limite: 2259.20, aliquota: 0, parcelaDeducao: 0 },
  { limite: 2826.65, aliquota: 0.075, parcelaDeducao: 169.44 },
  { limite: 3751.05, aliquota: 0.15, parcelaDeducao: 381.44 },
  { limite: 4664.68, aliquota: 0.225, parcelaDeducao: 662.77 },
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 896.00 },
] as const;

// Tabela IRRF 2026 - PROPOSTA (PL 1.087/2025)
export const TABELA_IRRF_2026_PROPOSTA = [
  { limite: 2428.80, aliquota: 0, parcelaDeducao: 0 },
  { limite: 2826.65, aliquota: 0.075, parcelaDeducao: 182.16 },
  { limite: 3751.05, aliquota: 0.15, parcelaDeducao: 394.16 },
  { limite: 4664.68, aliquota: 0.225, parcelaDeducao: 675.49 },
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 908.72 },
] as const;

// Tabela INSS 2025
export const TABELA_INSS_2025 = [
  { limite: 1518.0, aliquota: 0.075, parcelaDeducao: 0 },
  { limite: 2793.88, aliquota: 0.09, parcelaDeducao: 22.77 },
  { limite: 4190.83, aliquota: 0.12, parcelaDeducao: 106.59 },
  { limite: 8157.41, aliquota: 0.14, parcelaDeducao: 190.4 },
] as const;

// ============================================
// CONSTANTES DO PROJETO DE LEI 1.087/2025
// ============================================

export const CONSTANTES_ISENCAO_IR_2026 = {
  // Faixa de redução máxima (até R$ 5.000)
  REDUCAO_MAXIMA: {
    LIMITE_BASE: 5000.00,
    VALOR_REDUCAO: 312.89,
  },
  
  // Faixa de redução decrescente - Substitutivo Arthur Lira
  REDUCAO_PROGRESSIVA: {
    LIMITE_BASE_INFERIOR: 5000.01,
    LIMITE_BASE_SUPERIOR: 7350.00,
    CONSTANTE_FIXA: 978.61,
    COEFICIENTE: 0.133145,
  },
  
  // Valores anuais
  ANUAL: {
    BASE_INICIO_REDUCAO: 36432.00,
    LIMITE_REDUCAO_MAXIMA: 60000.00,
    VALOR_REDUCAO_MAXIMA: 2694.15,
    LIMITE_REDUCAO_SUPERIOR: 84000.00,
    CONSTANTE_FIXA: 9429.52,
    COEFICIENTE: 0.1122562,
  },
} as const;

// ============================================
// CONSTANTES GERAIS
// ============================================

export const CONSTANTES_GERAIS = {
  CALCULO: {
    DECIMAL_PRECISION: 100,
    MIN_VALUE: 0,
    MESES_ANO: 12,
    PARCELAS_ANUAIS: 13,
  },
  
  VALIDACAO: {
    SALARIO_MINIMO: 0.01,
    SALARIO_MAXIMO_SUGERIDO: 50000.00,
  },
  
  INSS: {
    TETO_CONTRIBUICAO: 8157.41,
  },
} as const;

// ============================================
// MENSAGENS DE VALIDAÇÃO
// ============================================

export const ValidationMessages = {
  SALARIO_OBRIGATORIO: "Informe o salário bruto mensal.",
  SALARIO_INVALIDO: "Salário bruto deve ser maior que zero.",
  SALARIO_MUITO_ALTO: "Valor muito alto. Verifique se digitou corretamente.",
  ERRO_CALCULO: "Erro no cálculo. Verifique os dados informados.",
} as const;

// ============================================
// MENSAGENS DE RESULTADO
// ============================================

export const ResultMessages = {
  JA_ISENTO: {
    TITULO: "Você já está na faixa de isenção atual",
    DESCRICAO: "Seu salário está dentro da faixa de isenção vigente (até R$ 2.259,20). Você não paga IR atualmente e continuará isento com a nova regra.",
  },
  
  BENEFICIADO_TOTAL: {
    TITULO: "Você será beneficiado com redução do IR!",
    DESCRICAO: "Com a nova regra, você terá redução no imposto devido (base de cálculo até R$ 5.000).",
  },
  
  BENEFICIADO_PARCIAL: {
    TITULO: "Você será beneficiado com redução parcial no IR!",
    DESCRICAO: "Com a nova regra, você terá uma redução progressiva no imposto devido (base de cálculo entre R$ 5.000,01 e R$ 7.350).",
  },
  
  NAO_BENEFICIADO: {
    TITULO: "Sem redução para esta faixa salarial",
    DESCRICAO: "Sua base de cálculo está acima de R$ 7.350. A nova regra não prevê redução de IR para esta faixa, mas você continuará contribuindo normalmente.",
  },
} as const;

// ============================================
// INFORMAÇÕES DO PROJETO DE LEI
// ============================================

export const INFO_PROJETO_LEI = {
  NUMERO: "PL 1.087/2025",
  DATA_APROVACAO_CAMARA: "01/10/2025",
  VOTOS_FAVORAVEIS: 493,
  VOTOS_CONTRARIOS: 0,
  STATUS: "Lei aprovada",
  VIGENCIA_PREVISTA: "Janeiro de 2026",
  BENEFICIARIOS_ESTIMADOS: "26,6 milhões",
} as const;

// ============================================
// TIPOS E INTERFACES
// ============================================

export type TabelaImpostoItem = {
  readonly limite: number;
  readonly aliquota: number;
  readonly parcelaDeducao: number;
};

export type FaixaBeneficio = 
  | 'JA_ISENTO'
  | 'BENEFICIADO_TOTAL'
  | 'BENEFICIADO_PARCIAL'
  | 'NAO_BENEFICIADO';

export interface INSSResultType {
  readonly valor: number;
  readonly baseCalculo: number;
  readonly aliquotaEfetiva: number;
}

export interface IRResultType {
  readonly valor: number;
  readonly baseCalculo: number;
  readonly aliquota: number;
}

export interface IsencaoIRResultType {
  readonly salarioBruto: number;
  readonly inssDescontado: number;
  readonly baseCalculoIR: number;
  readonly irAtual: number;
  readonly irProposto: number;
  readonly reducaoAplicada: number;
  readonly economiaMensal: number;
  readonly economiaAnual: number;
  readonly percentualEconomia: number;
  readonly faixaBeneficio: FaixaBeneficio;
  readonly aliquotaAtual: number;
  readonly aliquotaProposta: number;
}